import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  Send,
  MessageCircle,
  Clock,
  CheckCircle2,
  ShieldCheck,
  Clock3,
  Banknote,
} from "lucide-react";
import { BRAND } from "../data/site";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    destination: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;

    const lines = [
      `Hello Happy Journey 4U!`,
      `My name is *${form.name}* and I'd like to plan a trip.`,
      `📞 Phone: ${form.phone}`,
    ];
    if (form.destination) lines.push(`📍 Destination: ${form.destination}`);
    if (form.message) lines.push(`📝 Message: ${form.message}`);
    lines.push("", "(Sent via happyjourney4u.co.in)");

    setRedirecting(true);
    setTimeout(() => {
      const url = `https://wa.me/${BRAND.phones[0].raw}?text=${encodeURIComponent(
        lines.join("\n")
      )}`;
      window.open(url, "_blank", "noopener");
      setSent(true);
      setRedirecting(false);
      setTimeout(() => setSent(false), 4500);
      setForm({ name: "", phone: "", destination: "", message: "" });
    }, 800);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 aurora-bg grain"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-xs uppercase tracking-[0.24em] text-brand-orange font-semibold">
            Let's Plan Your Journey
          </div>
          <h2
            data-testid="contact-heading"
            className="mt-3 font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] tracking-tight text-balance"
          >
            Tell us your dream — we'll do the{" "}
            <span className="italic text-brand-orange">heavy lifting</span>.
          </h2>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed">
            Drop your details and our travel team will WhatsApp you a
            personalised quote within hours.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-8">
          {/* Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 glass rounded-3xl p-7 sm:p-10 shadow-xl"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field
                id="name"
                label="Your Name"
                placeholder="e.g. Sharma family, Surat"
                value={form.name}
                onChange={(v) => update("name", v)}
                required
              />
              <Field
                id="phone"
                label="Phone / WhatsApp"
                placeholder="e.g. +91 98765 43210"
                value={form.phone}
                onChange={(v) => update("phone", v)}
                type="tel"
                required
              />
              <Field
                id="destination"
                label="Preferred Destination"
                placeholder="e.g. Bali, Dubai, Kashmir…"
                value={form.destination}
                onChange={(v) => update("destination", v)}
                className="sm:col-span-2"
              />
              <div className="sm:col-span-2">
                <label
                  htmlFor="message"
                  className="block text-xs uppercase tracking-[0.2em] text-slate-600 font-semibold mb-2"
                >
                  Tell us a bit more
                </label>
                <textarea
                  id="message"
                  data-testid="contact-form-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Travel dates, group size, budget, special requests…"
                  className="w-full rounded-2xl bg-white/80 border border-white/60 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/30 outline-none px-4 py-3 text-slate-900 placeholder-slate-400 transition-all resize-none focus-visible:outline-none"
                />
              </div>
            </div>

            <div className="mt-7 flex flex-col gap-4">
              <button
                data-testid="contact-form-submit"
                type="submit"
                disabled={redirecting}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-brand-orange text-white font-semibold shadow-[0_14px_40px_rgba(243,146,0,0.45)] hover:bg-brand-orangeDark hover:shadow-[0_18px_50px_rgba(243,146,0,0.55)] transition-all disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 w-full sm:w-auto"
              >
                {redirecting ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Opening WhatsApp…
                  </>
                ) : sent ? (
                  <>
                    <CheckCircle2 className="w-5 h-5" /> Sent! Chat opened ✓
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> Send via WhatsApp
                  </>
                )}
              </button>
              {/* Helper text */}
              <p className="text-xs text-slate-500 leading-relaxed">
                <MessageCircle className="inline w-3.5 h-3.5 mr-1 text-[#25D366]" />
                You will be redirected to WhatsApp with a pre-filled message to our team. No spam — promised.
              </p>
            </div>
          </motion.form>

          {/* Contact info card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 flex flex-col gap-6"
          >
            <div className="glass-dark rounded-3xl p-7 text-white">
              <div className="text-xs uppercase tracking-[0.22em] text-brand-orange font-semibold">
                Reach us directly
              </div>
              <h3 className="mt-2 font-playfair text-3xl font-bold leading-tight">
                We're open every day.
              </h3>

              <div className="mt-7 space-y-5">
                <InfoRow icon={MapPin} label="Our Office">
                  {BRAND.address}
                </InfoRow>

                <InfoRow icon={Phone} label="Call us">
                  <div className="flex flex-col gap-1">
                    {BRAND.phones.map((p) => (
                      <a
                        key={p.number}
                        href={`tel:${p.number.replace(/\s/g, "")}`}
                        data-testid={`contact-phone-${p.label.toLowerCase()}`}
                        className="hover:text-brand-orange transition-colors focus-visible:outline-none focus-visible:underline"
                      >
                        {p.number}
                      </a>
                    ))}
                  </div>
                </InfoRow>

                <InfoRow icon={Mail} label="Email">
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="hover:text-brand-orange transition-colors focus-visible:outline-none focus-visible:underline"
                  >
                    {BRAND.email}
                  </a>
                </InfoRow>

                <InfoRow icon={Clock} label="Working hours">
                  Mon–Sun · 9:00 AM – 9:00 PM
                </InfoRow>
              </div>

              <a
                href={`https://wa.me/${BRAND.phones[0].raw}?text=${encodeURIComponent(
                  "Hi Happy Journey 4U!"
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-direct"
                className="mt-8 inline-flex items-center gap-2 w-full justify-center px-5 py-3 rounded-2xl bg-[#25D366] hover:bg-[#1eb858] text-white font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]"
              >
                <MessageCircle className="w-5 h-5" />
                Chat on WhatsApp now
              </a>
            </div>

            {/* Reassurance bullets */}
            <div className="glass rounded-2xl p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">
                Why enquire with us?
              </p>
              <ul className="space-y-3">
                {[
                  { icon: ShieldCheck, text: "No hidden charges — ever" },
                  { icon: Clock3, text: "Response within 2–3 hours" },
                  { icon: Banknote, text: "Best price guarantee" },
                  { icon: MessageCircle, text: "Direct chat with the owner" },
                ].map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-center gap-3 text-sm text-slate-700">
                    <Icon className="w-4 h-4 text-brand-ocean shrink-0" />
                    {text}
                  </li>
                ))}
              </ul>
            </div>

            {/* Map */}
            <div
              data-testid="contact-map"
              className="rounded-3xl overflow-hidden shadow-xl ring-1 ring-white/40 h-56"
            >
              <iframe
                title="Happy Journey 4U Office"
                src={BRAND.mapsEmbed}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen=""
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({
  id,
  label,
  placeholder,
  value,
  onChange,
  type = "text",
  required,
  className = "",
}) {
  return (
    <div className={className}>
      <label
        htmlFor={id}
        className="block text-xs uppercase tracking-[0.2em] text-slate-600 font-semibold mb-2"
      >
        {label}
        {required && <span className="text-brand-orange ml-1" aria-label="required">*</span>}
      </label>
      <input
        id={id}
        data-testid={`contact-form-${id}`}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-2xl bg-white/80 border border-white/60 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/30 outline-none px-4 py-3 text-slate-900 placeholder-slate-400 transition-all focus-visible:outline-none"
      />
    </div>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-4">
      <div className="h-11 w-11 rounded-xl bg-white/10 ring-1 ring-white/20 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-brand-orange" />
      </div>
      <div className="flex-1">
        <div className="text-[0.7rem] uppercase tracking-[0.2em] text-white/55">
          {label}
        </div>
        <div className="mt-0.5 text-white/90 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
